/* Contrato de RELEASES do app (ADR-0659). O admin registra/promove/tira-do-ar versões do app cujos
   binários vivem no R2; o app pergunta "há update? é obrigatória?" via `/v1/updates/check`.
   Compartilhado entre admin-service e admin-web. */
import { z } from 'zod';

export const RELEASE_CHANNELS = ['stable', 'beta', 'canary'] as const;
export const releaseChannelSchema = z.enum(RELEASE_CHANNELS);
export type ReleaseChannel = z.infer<typeof releaseChannelSchema>;

export const RELEASE_PLATFORMS = ['mac', 'win', 'linux'] as const;
export const releasePlatformSchema = z.enum(RELEASE_PLATFORMS);
export type ReleasePlatform = z.infer<typeof releasePlatformSchema>;

export const RELEASE_ARCHES = ['x64', 'arm64', 'universal'] as const;
export const releaseArchSchema = z.enum(RELEASE_ARCHES);
export type ReleaseArch = z.infer<typeof releaseArchSchema>;

export const RELEASE_STATUSES = ['draft', 'promoted', 'yanked'] as const;
export const releaseStatusSchema = z.enum(RELEASE_STATUSES);
export type ReleaseStatus = z.infer<typeof releaseStatusSchema>;

/** Estado da limpeza física do prefixo canônico da versão no R2 (ADR-0908). */
export const RELEASE_STORAGE_STATES = ['present', 'deleting', 'deleted'] as const;
export const releaseStorageStateSchema = z.enum(RELEASE_STORAGE_STATES);
export type ReleaseStorageState = z.infer<typeof releaseStorageStateSchema>;

/**
 * Papel do artefato na distribuição.
 *
 * A distinção existe porque os dois públicos querem arquivos DIFERENTES, e o registry só
 * conhecia um deles. `electron-updater` baixa um formato específico por plataforma
 * (mac `.zip`, win `.exe`, linux `.AppImage`); um humano quer o instalador do seu SO
 * (mac `.dmg`, linux `.deb`/`.rpm`). Antes desta distinção, `/v1/updates/download?os=mac`
 * entregava o `.zip` — que o visitante tem de descompactar e arrastar à mão.
 *
 * `updater` é também instalável em win e linux (o `.exe` do nsis e o `.AppImage` são as duas
 * coisas), então o download para humano PREFERE `installer` e cai em `updater` quando não há.
 * O feed do electron-updater, ao contrário, só pode listar `updater` — um `.dmg` no `latest-mac.yml`
 * quebraria a atualização automática.
 */
export const RELEASE_KINDS = ['updater', 'installer'] as const;
export const releaseKindSchema = z.enum(RELEASE_KINDS);
export type ReleaseKind = z.infer<typeof releaseKindSchema>;

/**
 * Formato físico do artefato.
 *
 * `kind` descreve o papel no produto; `format` identifica o arquivo real. Essa separação é
 * obrigatória no Linux, onde `.deb` e `.rpm` são ambos `installer` para a mesma plataforma/arch,
 * mas precisam permanecer duas opções distintas no índice e no backoffice.
 */
export const RELEASE_FORMATS = ['dmg', 'zip', 'exe', 'appimage', 'deb', 'rpm'] as const;
export const releaseFormatSchema = z.enum(RELEASE_FORMATS);
export type ReleaseFormat = z.infer<typeof releaseFormatSchema>;

interface ReleaseArtifactIdentity {
  readonly platform: ReleasePlatform;
  readonly arch: ReleaseArch;
  readonly kind: ReleaseKind;
  readonly format: ReleaseFormat;
}

/** Matriz única do contrato: formato físico e papel não podem contradizer a plataforma. */
const RELEASE_FORMAT_KIND: Readonly<
  Record<ReleasePlatform, Partial<Readonly<Record<ReleaseFormat, ReleaseKind>>>>
> = {
  mac: { dmg: 'installer', zip: 'updater' },
  win: { exe: 'updater', zip: 'installer' },
  linux: { appimage: 'updater', deb: 'installer', rpm: 'installer' },
};

function validateReleaseArtifactIdentity(
  artifact: ReleaseArtifactIdentity,
  context: z.RefinementCtx,
): void {
  const expectedKind = RELEASE_FORMAT_KIND[artifact.platform][artifact.format];
  if (expectedKind === undefined) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['format'],
      message: 'release_artifact_format_platform_mismatch',
    });
    return;
  }
  if (artifact.kind !== expectedKind) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['kind'],
      message: 'release_artifact_kind_format_mismatch',
    });
  }
  if (artifact.arch === 'universal' && artifact.platform === 'linux') {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['arch'],
      message: 'release_artifact_universal_linux_unsupported',
    });
  }
}

export const RELEASE_NOTES_MAX_LENGTH = 5000;
export const RELEASE_METADATA_FILE = 'release-metadata.json';
export const RELEASE_ARTIFACTS_FILE = 'release-artifacts.json';
const KEY_MAX_LEN = 300;
/* Semver simples (major.minor.patch com pré-release opcional). Não valida build metadata. */
const semverSchema = z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z-.]+)?$/, 'invalid_semver');
/* SHA-512 sempre ocupa 64 bytes; em base64 canônico são 86 símbolos e `==` de padding. */
const sha512Base64Schema = z.string().regex(/^[A-Za-z0-9+/]{85}[AQgw]==$/, 'invalid_sha512_base64');

const releaseShape = {
  id: z.string().min(1),
  version: semverSchema,
  channel: releaseChannelSchema,
  platform: releasePlatformSchema,
  arch: releaseArchSchema,
  kind: releaseKindSchema,
  format: releaseFormatSchema,
  fileName: z.string().min(1).max(KEY_MAX_LEN),
  r2Key: z.string().min(1).max(KEY_MAX_LEN),
  sha512: z.string().min(1),
  sizeBytes: z.number().int().positive(),
  status: releaseStatusSchema,
  mandatory: z.boolean(),
  minVersion: semverSchema.nullable(),
  releaseNotes: z.string().nullable(),
  createdAt: z.string(),
  promotedAt: z.string().nullable(),
};

/** Release completa (visão do admin). */
export const releaseDto = z.object(releaseShape).superRefine(validateReleaseArtifactIdentity);
export type Release = z.infer<typeof releaseDto>;

/* Filtros + keyset da lista paginada de releases (aba "Releases" do backoffice). Espelha
   `listCompaniesQuery`/`auditQuerySchema`: TODOS os filtros são server-side — client-side não
   funciona com paginação (só a página atual chega ao cliente). `channel`/`status` são igualdade
   exata; `mandatory` é o tri-estado do UI (todas/obrigatórias/opcionais); `platform` é o
   multi-select como CSV (`mac,win`); `search` é ILIKE "contém" sobre a versão. */
export const listReleasesQuery = z.object({
  channel: releaseChannelSchema.optional(),
  status: releaseStatusSchema.optional(),
  /* Tri-estado no UI → boolean opcional. `z.coerce.boolean` trataria "false" como truthy
     (string não-vazia) → preprocess explícito; valor ausente/inválido = sem filtro. */
  mandatory: z.preprocess(
    (v) => (v === 'true' ? true : v === 'false' ? false : undefined),
    z.boolean().optional(),
  ),
  /* Multi-select de plataforma como CSV; vazio/omisso = sem filtro. */
  platform: z.preprocess(
    (v) => (typeof v === 'string' && v.length > 0 ? v.split(',') : undefined),
    z.array(releasePlatformSchema).optional(),
  ),
  /* Busca por substring de versão (ILIKE "contém"). */
  search: z.string().min(1).max(100).optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListReleasesQuery = z.infer<typeof listReleasesQuery>;

/**
 * Release + métrica de downloads (visão do backoffice).
 *
 * Extensão em vez de campo no `releaseDto` porque as duas superfícies têm públicos opostos: o feed
 * do electron-updater e o `/check` leem a release a cada request e não têm o que fazer com
 * contagem, enquanto a lista do staff existe justamente para mostrá-la. Só a lista paga o custo do
 * join.
 *
 * `downloadCount` conta os downloads servidos por `/v1/updates/download` — o clique humano. As
 * atualizações automáticas NÃO entram: o `latest*.yml` carrega URLs absolutas do R2 e o
 * electron-updater baixa direto do bucket, sem passar pelo Worker.
 */
export const releaseWithMetricsDto = z
  .object({
    ...releaseShape,
    downloadCount: z.number().int().nonnegative(),
    lastDownloadAt: z.string().nullable(),
    /* Defaults mantêm respostas antigas parseáveis durante o rollout, mas o output tipado sempre
       explicita o tombstone e os totais persistidos da limpeza física. */
    storageState: releaseStorageStateSchema.default('present'),
    storageObjectCount: z.number().int().nonnegative().default(0),
    storageBytesFreed: z.number().int().nonnegative().default(0),
  })
  .superRefine(validateReleaseArtifactIdentity);
export type ReleaseWithMetrics = z.infer<typeof releaseWithMetricsDto>;

/* Página keyset da lista de releases (`items` + `nextCursor` opaco), espelhando `auditLogPageDto`.
   Usada tanto na validação de saída da rota (ADR-0708) quanto no parse do cliente REST. */
export const releasesPageDto = z.object({
  items: z.array(releaseWithMetricsDto),
  nextCursor: z.string().nullish(),
});
export type ReleasesPage = z.infer<typeof releasesPageDto>;

/** Objeto cru listado do bucket R2 (o que já subiu, mesmo sem registro). */
export const bucketObjectDto = z.object({
  key: z.string(),
  size: z.number().int().nonnegative(),
  uploaded: z.string(),
});
export const bucketListDto = z.object({ objects: z.array(bucketObjectDto) });
export type BucketList = z.infer<typeof bucketListDto>;

/* Registrar release (staff manual OU CI interno): metadados apontando p/ o objeto R2. Nasce `draft`
   — só existir no R2 não a torna disponível (ADR-0659). */
export const registerReleaseBody = z
  .object({
    version: semverSchema,
    channel: releaseChannelSchema,
    platform: releasePlatformSchema,
    arch: releaseArchSchema,
    /* `.default('updater')` e não obrigatório: todo caller anterior a esta distinção registrava
       artefato de atualização, então o default preserva o significado do que já existe. */
    kind: releaseKindSchema.default('updater'),
    format: releaseFormatSchema,
    fileName: z.string().min(1).max(KEY_MAX_LEN),
    r2Key: z.string().min(1).max(KEY_MAX_LEN),
    sha512: sha512Base64Schema,
    sizeBytes: z.number().int().positive(),
    releaseNotes: z.string().max(RELEASE_NOTES_MAX_LENGTH).optional(),
  })
  .superRefine(validateReleaseArtifactIdentity);
export type RegisterReleaseBody = z.infer<typeof registerReleaseBody>;

/* DESCOBERTA de release a partir do bucket (ADR-0670). O layout é versionado — `<canal>/<versão>/` —
   e cada versão carrega os `latest*.yml` que o electron-builder gerou (fonte de verdade de
   sha512/tamanho). O staff informa canal+versão; o service lê a pasta, parseia os manifestos e
   devolve os artefatos por plataforma/arch já preenchidos — sem digitar path/hash à mão. */

/* Versões presentes no bucket (pastas `<canal>/<versão>/`), p/ o admin ESCOLHER em vez de digitar. */
export const availableVersionsQuery = z.object({
  channel: releaseChannelSchema.default('stable'),
});
export type AvailableVersionsQuery = z.infer<typeof availableVersionsQuery>;

export const availableVersionsDto = z.object({
  channel: releaseChannelSchema,
  versions: z.array(semverSchema),
});
export type AvailableVersions = z.infer<typeof availableVersionsDto>;

export const discoverReleaseQuery = z.object({
  version: semverSchema,
  channel: releaseChannelSchema.default('stable'),
});
export type DiscoverReleaseQuery = z.infer<typeof discoverReleaseQuery>;

/** Sidecar determinístico publicado uma vez por tag em `<channel>/<version>/`. */
export const releaseMetadataDto = z.object({
  version: semverSchema,
  channel: releaseChannelSchema,
  releaseNotes: z.string().max(RELEASE_NOTES_MAX_LENGTH),
  warnings: z.array(z.string()),
});
export type ReleaseMetadata = z.infer<typeof releaseMetadataDto>;

/* Metadados de um artefato descoberto no bucket. Releases novas usam o inventário completo;
   versões legadas usam os feeds do builder. `kind` descreve o papel e `format` o arquivo físico. */
const discoveredArtifactShape = {
  platform: releasePlatformSchema,
  arch: releaseArchSchema,
  kind: releaseKindSchema,
  format: releaseFormatSchema,
  fileName: z.string().min(1).max(KEY_MAX_LEN),
  r2Key: z.string().min(1).max(KEY_MAX_LEN),
  sha512: z.string().min(1),
  sizeBytes: z.number().int().positive(),
};

export const discoveredArtifactMeta = z
  .object(discoveredArtifactShape)
  .superRefine(validateReleaseArtifactIdentity);
export type DiscoveredArtifactMeta = z.infer<typeof discoveredArtifactMeta>;

/** Inventário canônico produzido pelo publisher para TODOS os distribuíveis. Diferente dos
    `latest*.yml`, ele também inclui formatos que não participam do auto-update (ex.: ZIP Windows). */
export const releaseArtifactInventoryItemDto = z
  .object({
    platform: discoveredArtifactShape.platform,
    arch: discoveredArtifactShape.arch,
    kind: discoveredArtifactShape.kind,
    format: discoveredArtifactShape.format,
    fileName: discoveredArtifactShape.fileName,
    sha512: discoveredArtifactShape.sha512,
    sizeBytes: discoveredArtifactShape.sizeBytes,
  })
  .superRefine(validateReleaseArtifactIdentity);
export type ReleaseArtifactInventoryItem = z.infer<typeof releaseArtifactInventoryItemDto>;

export const releaseArtifactsManifestDto = z.object({
  version: semverSchema,
  channel: releaseChannelSchema,
  artifacts: z.array(releaseArtifactInventoryItemDto).min(1),
});
export type ReleaseArtifactsManifest = z.infer<typeof releaseArtifactsManifestDto>;

/* `alreadyRegistered`: já existe uma linha em `app_releases` p/ {versão,canal,plataforma,arch} — a UI
   desabilita o item (o registro em lote é idempotente e o pula). */
export const discoveredArtifactDto = z
  .object({ ...discoveredArtifactShape, alreadyRegistered: z.boolean() })
  .superRefine(validateReleaseArtifactIdentity);
export type DiscoveredArtifact = z.infer<typeof discoveredArtifactDto>;

export const discoverReleaseDto = z.object({
  version: semverSchema,
  channel: releaseChannelSchema,
  artifacts: z.array(discoveredArtifactDto),
  /* Sidecar ausente/inválido não bloqueia a descoberta: o operador pode escrever manualmente. */
  suggestedReleaseNotes: z.string().max(RELEASE_NOTES_MAX_LENGTH).nullable(),
  /** Nota canônica já persistida; permite salvar um sidecar tardio sem inventar novo artefato. */
  registeredReleaseNotes: z.string().max(RELEASE_NOTES_MAX_LENGTH).nullable(),
});
export type DiscoverRelease = z.infer<typeof discoverReleaseDto>;

/* Registrar em LOTE os artefatos descobertos (todos nascem `draft`). Idempotente: artefatos já
   registrados são pulados (não falha o lote). */
export const registerDiscoveredBody = z.object({
  version: semverSchema,
  channel: releaseChannelSchema,
  artifacts: z.array(discoveredArtifactMeta).min(1),
  releaseNotes: z.string().max(RELEASE_NOTES_MAX_LENGTH).optional(),
});
export type RegisterDiscoveredBody = z.infer<typeof registerDiscoveredBody>;

export const registerDiscoveredResultDto = z.object({
  registered: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type RegisterDiscoveredResult = z.infer<typeof registerDiscoveredResultDto>;

/* Promover: draft/yanked → promoted. `mandatory` marca esta versão como piso obrigatório do canal
   (clients abaixo dela são bloqueados). */
export const promoteReleaseBody = z.object({
  mandatory: z.boolean().default(false),
});
export type PromoteReleaseBody = z.infer<typeof promoteReleaseBody>;

/**
 * Remove a linha do índice administrativo sem apagar o artefato publicado (ADR-0825).
 *
 * `objectDeleted` permanece no wire por compatibilidade, mas a operação normal sempre devolve
 * `false`: prefixos concluídos no R2 e assets da release do GitHub são imutáveis. O objeto permite
 * redescobrir e reparar um registro removido.
 */
export const deleteReleaseResultDto = z.object({
  id: z.string().min(1),
  objectDeleted: z.boolean(),
});
export type DeleteReleaseResult = z.infer<typeof deleteReleaseResultDto>;

/**
 * Ações em lote da tela de versões.
 *
 * `delete` remove índice e métricas, mas preserva os objetos publicados. A UI confirma em Dialog
 * dizendo QUANTOS itens serão afetados, e o service recusa por item toda release `promoted`
 * (`release_is_promoted`): desindexar a fonte que o feed usa quebraria o auto-update. Tirar do ar
 * primeiro (`takedown`) é o caminho; depois, rediscovery pode reconstruir o registro.
 */
export const RELEASE_BULK_ACTIONS = ['promote', 'promoteMandatory', 'takedown', 'delete'] as const;
export const releaseBulkActionSchema = z.enum(RELEASE_BULK_ACTIONS);
export type ReleaseBulkAction = z.infer<typeof releaseBulkActionSchema>;

/** Ações que destroem dado — a UI exige confirmação explícita antes de aplicar. */
export const DESTRUCTIVE_RELEASE_BULK_ACTIONS: readonly ReleaseBulkAction[] = ['delete'];

/* Teto por requisição: o handler abre uma transação de status + mutação por item, e um lote sem
   limite estouraria o tempo de CPU/conexão do Worker. Espelha o teto da página da lista. */
export const RELEASE_BULK_MAX_IDS = 100;

export const bulkReleaseActionBody = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .max(RELEASE_BULK_MAX_IDS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate_bulk_ids' }),
  action: releaseBulkActionSchema,
});
export type BulkReleaseActionBody = z.infer<typeof bulkReleaseActionBody>;

/* Resultado POR ITEM (espelha `bulkPublisherActionResponseDto`): o lote é parcialmente aplicável —
   uma release promovida no meio da seleção falha só a si mesma, sem abortar as demais. `error`
   carrega o código de negócio (`release_is_promoted`/`release_not_found`), nunca texto. */
export const bulkReleaseActionResponseDto = z.object({
  results: z.array(z.object({ id: z.string(), ok: z.boolean(), error: z.string().nullable() })),
});
export type BulkReleaseActionResponse = z.infer<typeof bulkReleaseActionResponseDto>;

/* Resposta do `/v1/updates/check` consumida pelo app. */
export const updateCheckDto = z.object({
  current: z.string(),
  latest: semverSchema.nullable(),
  minVersion: semverSchema.nullable(),
  mandatory: z.boolean(),
  blocked: z.boolean(),
});
export type UpdateCheck = z.infer<typeof updateCheckDto>;

export const updateCheckQuery = z.object({
  os: releasePlatformSchema,
  arch: releaseArchSchema.optional(),
  channel: releaseChannelSchema.default('stable'),
  version: semverSchema,
});
export type UpdateCheckQuery = z.infer<typeof updateCheckQuery>;

/** Seleção pública do instalador promovido que o dispositivo consegue executar. */
export const updateArtifactQuery = z.object({
  os: z.enum(['mac', 'win']),
  arch: releaseArchSchema,
  channel: releaseChannelSchema.default('stable'),
});
export type UpdateArtifactQuery = z.infer<typeof updateArtifactQuery>;

/**
 * Metadados necessários para uma instalação assistida.
 *
 * O objeto deliberadamente não carrega `r2Key`: baixar continua sendo responsabilidade do
 * endpoint dedicado, enquanto esta consulta apenas permite ao app explicar e confirmar o
 * artefato promovido que será instalado.
 */
export const updateArtifactDto = z.object({
  version: semverSchema,
  arch: releaseArchSchema,
  format: z.enum(['dmg', 'exe']),
  fileName: z.string().min(1).max(KEY_MAX_LEN),
  sha512: sha512Base64Schema,
  sizeBytes: z.number().int().positive(),
  releaseNotes: z.string().max(RELEASE_NOTES_MAX_LENGTH).nullable(),
});
export type UpdateArtifact = z.infer<typeof updateArtifactDto>;

/* Editar as notas de uma release (o admin gerencia o changelog pós-registro). `null` limpa. */
export const updateReleaseNotesBody = z.object({
  releaseNotes: z.string().max(RELEASE_NOTES_MAX_LENGTH).nullable(),
});
export type UpdateReleaseNotesBody = z.infer<typeof updateReleaseNotesBody>;

/** Política e notas pertencem à versão, não ao arquivo: uma chamada atualiza todos os irmãos. */
export const updateReleaseVersionBody = z
  .object({
    mandatory: z.boolean().optional(),
    releaseNotes: z.string().max(RELEASE_NOTES_MAX_LENGTH).nullable().optional(),
  })
  .refine((body) => body.mandatory !== undefined || body.releaseNotes !== undefined, {
    message: 'empty_release_version_update',
  });
export type UpdateReleaseVersionBody = z.infer<typeof updateReleaseVersionBody>;

export const releaseVersionParams = z.object({
  channel: releaseChannelSchema,
  version: semverSchema,
});
export type ReleaseVersionParams = z.infer<typeof releaseVersionParams>;

/** Confirmação forte da exclusão física version-scoped (ADR-0908). */
export const purgeReleaseVersionBody = z.object({
  confirmation: z.string().min(1).max(100),
  reason: z.string().trim().min(3).max(500),
});
export type PurgeReleaseVersionBody = z.infer<typeof purgeReleaseVersionBody>;

/** Resultado persistido após o prefixo inteiro desaparecer do R2. */
export const purgeReleaseVersionResultDto = z
  .object({
    channel: releaseChannelSchema,
    version: semverSchema,
    prefix: z.string().min(1).max(KEY_MAX_LEN),
    objectsDeleted: z.number().int().nonnegative(),
    bytesDeleted: z.number().int().nonnegative(),
    storageState: z.literal('deleted'),
  })
  .superRefine((result, context) => {
    if (result.prefix !== `${result.channel}/${result.version}/`) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prefix'],
        message: 'release_storage_prefix_mismatch',
      });
    }
  });
export type PurgeReleaseVersionResult = z.infer<typeof purgeReleaseVersionResultDto>;

/* Entrada do CHANGELOG lida pelo app (tela "Versões"): uma linha por versão PROMOVIDA do canal, com
   as notas em markdown. `releasedAt` = quando foi promovida. */
export const availableReleaseArtifactDto = z
  .object({
    platform: releasePlatformSchema,
    arch: releaseArchSchema,
    format: releaseFormatSchema,
    kind: releaseKindSchema,
  })
  .superRefine(validateReleaseArtifactIdentity);
export type AvailableReleaseArtifact = z.infer<typeof availableReleaseArtifactDto>;

export const versionEntryDto = z.object({
  version: semverSchema,
  channel: releaseChannelSchema,
  releaseNotes: z.string().max(RELEASE_NOTES_MAX_LENGTH).nullable(),
  releasedAt: z.string().nullable(),
  /** Plataformas que têm ao menos um artefato promovido nesta versão. */
  availablePlatforms: z.array(releasePlatformSchema).min(1),
  /** Identidades públicas dos arquivos realmente promovidos. Sem nome/path/hash: a LP usa esta
      lista somente para oferecer escolhas exatas, e `/download` continua resolvendo o objeto. */
  availableArtifacts: z.array(availableReleaseArtifactDto).min(1),
});
export type VersionEntry = z.infer<typeof versionEntryDto>;

export const latestByPlatformDto = z.object({
  mac: semverSchema.nullable(),
  win: semverSchema.nullable(),
  linux: semverSchema.nullable(),
});
export type LatestByPlatform = z.infer<typeof latestByPlatformDto>;

export const versionListDto = z.object({
  versions: z.array(versionEntryDto),
  /** Latest é por plataforma: uma promoção parcial nunca vira um rótulo global enganoso. */
  latestByPlatform: latestByPlatformDto,
});
export type VersionList = z.infer<typeof versionListDto>;

export const versionListQuery = z.object({
  channel: releaseChannelSchema.default('stable'),
});
export type VersionListQuery = z.infer<typeof versionListQuery>;

/* `/v1/updates/download`: sem `version` resolve a maior promovida; com `version`, a versão exata
   promovida. Draft/yanked/ausente continuam indistinguíveis (404) na superfície pública. */
export const downloadQuery = z
  .object({
    os: releasePlatformSchema,
    arch: releaseArchSchema.optional(),
    format: releaseFormatSchema.optional(),
    channel: releaseChannelSchema.default('stable'),
    version: semverSchema.optional(),
    /** Modo exclusivo do CTA latest: a CPU informada aceita também artefato `universal`.
        Links históricos não usam esse modo porque sua identidade precisa continuar exata. */
    compatible: z
      .enum(['true'])
      .transform(() => true as const)
      .optional(),
    /** Quando presente, uma indisponibilidade volta para a rota fixa da LP nesse idioma. O service
        não aceita `returnTo`, URL ou path fornecido pelo caller. */
    locale: z.enum(['pt-BR', 'en-US']).optional(),
  })
  .superRefine((query, ctx) => {
    if (query.compatible !== true) return;
    if (query.arch === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['arch'],
        message: 'compatible_requires_arch',
      });
    }
    if (query.version !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['version'],
        message: 'compatible_is_latest_only',
      });
    }
    if (query.format !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['format'],
        message: 'compatible_selects_safe_format',
      });
    }
  });
export type DownloadQuery = z.infer<typeof downloadQuery>;
